// src/features/executive/insight/widgets/OccupationBar.tsx
import { Box, Chip, Stack, Typography, Divider, Tooltip } from '@mui/material'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import WidgetCard from 'features/executive/components/WidgetCard/WidgetCard'
import { formatVariance } from 'utils/executive.utils'
import { useOccupation } from '../hooks/use-occupation'
import RevenueModeSelect from './RevenueModeSelect'
import type { RevenueMode } from 'api/executive/executive.types'

const fmt = (n: number) =>
  new Intl.NumberFormat('th-TH', { maximumFractionDigits: 0 }).format(Math.round(n || 0))

const fmtBaht = (n: number) => `฿${fmt(n)}`

// Rollup rows get muted styling so they don't compete with admin-named buckets.
const isRollup = (key: string) =>
  key === 'ungrouped_rollup' || key === 'other_rollup' || key === 'unknown'

// Data-quality threshold for "ไม่ระบุอาชีพ". Higher than country/nationality
// (10%) because careerId is genuinely optional in the patient form and many
// clinics never collect it — flag only when it's a clear majority of records.
const UNKNOWN_WARNING_PCT = 20

// Surface a positive insight when any non-rollup bucket grew by ≥20%
// vs the same-duration previous window. Helps execs spot emerging segments.
const TOP_MOVER_MIN_VARIANCE = 0.2

function varianceOf(current: number, previous: number | null | undefined): number | null {
  if (previous == null || previous === 0) return null
  return (current - previous) / Math.abs(previous)
}

export default function OccupationBar() {
  const { t } = useTranslation()
  const [revenueMode, setRevenueMode] = useState<RevenueMode>('fee')
  const { data, loading, error, refetch } = useOccupation(revenueMode)

  const segments = data?.segments ?? []
  const isEmpty = segments.length === 0
  const total = data?.total ?? 0
  const totalRevenue = data?.totalRevenue ?? 0
  const previousTotal = data?.previousTotal ?? null
  const previousTotalRevenue = data?.previousTotalRevenue ?? null
  const unknownCount = data?.unknownCount ?? 0

  const totalVariance = varianceOf(total, previousTotal)
  const revenueVariance = varianceOf(totalRevenue, previousTotalRevenue)

  // Use the largest patient-percent for bar-width normalization so even a
  // small leader fills the bar — keeps long-tail rows visible.
  const maxPct = segments.length === 0 ? 1 : Math.max(...segments.map((s) => s.percent), 1)

  // Data-quality nudge — unknown career share too high.
  const unknownPct = total > 0 ? (unknownCount / total) * 100 : 0
  const showUnknownWarning = unknownPct > UNKNOWN_WARNING_PCT

  // Top-mover: largest +variance among non-rollup buckets with prev>0.
  const topMover = segments
    .filter((s) => !isRollup(s.key) && s.previousCount != null && s.previousCount > 0)
    .map((s) => ({ s, v: varianceOf(s.count, s.previousCount) }))
    .filter((x) => x.v != null && (x.v as number) >= TOP_MOVER_MIN_VARIANCE)
    .sort((a, b) => (b.v as number) - (a.v as number))[0]

  const notice = (showUnknownWarning || topMover) ? (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            {showUnknownWarning && (
              <Chip
                icon={<WarningAmberOutlinedIcon />}
                label={
                  <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
                    {t(
                      'EXECUTIVE.INSIGHT.OCCUPATION.UNSPECIFIED_WARNING',
                      'ไม่ระบุอาชีพ {{pct}}% — แนะนำให้แอดมินตรวจสอบข้อมูล "อาชีพ" ของคนไข้',
                      { pct: unknownPct.toFixed(0) }
                    )}
                  </Typography>
                }
                color="warning"
                variant="outlined"
                size="small"
                sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
              />
            )}
      {topMover && (
        <Chip
          icon={<TrendingUpIcon />}
          label={
            <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
              {t(
                'EXECUTIVE.INSIGHT.OCCUPATION.TOP_MOVER',
                'อาชีพมาแรง: {{label}} ▲ {{pct}} ({{count}} คน)',
                {
                  label: topMover.s.label,
                  pct: formatVariance(topMover.v as number),
                  count: fmt(topMover.s.count),
                } as any
              )}
            </Typography>
          }
          color="info"
          variant="outlined"
          size="small"
          sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
        />
      )}
    </Stack>
  ) : null

  return (
    <WidgetCard
      title={t('EXECUTIVE.INSIGHT.OCCUPATION.TITLE')}
      titleTooltip={t(
        'EXECUTIVE.INSIGHT.OCCUPATION.SOURCE_TOOLTIP',
        'อ้างอิงจากช่อง "อาชีพ" ในใบลงทะเบียนของคนไข้ — ถ้าแอดมินตั้งค่ากลุ่มอาชีพไว้ที่ "ตั้งค่า → อาชีพ" จะแสดงตามกลุ่มที่กำหนด พร้อมกลุ่ม "ยังไม่จัดกลุ่ม" สำหรับอาชีพที่ยังไม่ได้จัด'
      )}
      subtitle={t('EXECUTIVE.INSIGHT.OCCUPATION.SUBTITLE')}
      headerAction={<RevenueModeSelect value={revenueMode} onChange={setRevenueMode} />}
      notice={notice}
      loading={loading}
      error={error}
      empty={isEmpty}
      emptyMessage={t(
        'EXECUTIVE.INSIGHT.OCCUPATION.EMPTY',
        'ไม่พบคนไข้ในช่วงเวลาและเงื่อนไขที่เลือก'
      )}
      onRetry={refetch}
      skeletonHeight={420}
    >
      {/* Summary strip: total patients + total revenue across all occupations, with variance */}
      <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('EXECUTIVE.INSIGHT.OCCUPATION.PATIENTS', { count: total })}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                {fmt(total)}
              </Typography>
              {totalVariance != null && (
                <Tooltip
                  title={t(
                    'EXECUTIVE.INSIGHT.PREV_PERIOD_TOOLTIP',
                    'ช่วงก่อนหน้า: {{prev}}',
                    { prev: fmt(previousTotal ?? 0) }
                  )}
                  arrow
                  placement="top"
                >
                  <Typography
                    variant="caption"
                    sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'help' }}
                    color={totalVariance >= 0 ? 'success.main' : 'error.main'}
                  >
                    {totalVariance >= 0
                      ? <TrendingUpIcon sx={{ fontSize: 14, mr: 0.25 }} />
                      : <TrendingDownIcon sx={{ fontSize: 14, mr: 0.25 }} />}
                    {formatVariance(totalVariance)}
                  </Typography>
                </Tooltip>
              )}
            </Box>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('EXECUTIVE.INSIGHT.OCCUPATION.REVENUE')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
              <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                {fmtBaht(totalRevenue)}
              </Typography>
              {revenueVariance != null && (
                <Tooltip
                  title={t(
                    'EXECUTIVE.INSIGHT.PREV_PERIOD_TOOLTIP_BAHT',
                    'ช่วงก่อนหน้า: ฿{{prev}}',
                    { prev: fmt(previousTotalRevenue ?? 0) }
                  )}
                  arrow
                  placement="top"
                >
                  <Typography
                    variant="caption"
                    sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'help' }}
                    color={revenueVariance >= 0 ? 'success.main' : 'error.main'}
                  >
                    {revenueVariance >= 0
                      ? <TrendingUpIcon sx={{ fontSize: 14, mr: 0.25 }} />
                      : <TrendingDownIcon sx={{ fontSize: 14, mr: 0.25 }} />}
                    {formatVariance(revenueVariance)}
                  </Typography>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Stack>

        <Stack spacing={1.5}>
          {segments.map((s) => {
            const variance = varianceOf(s.count, s.previousCount)
            return (
              <Box key={s.key}>
                {/* Top row: label + patient count/percent. Rollup row gets a
                    tooltip listing the careers it bundled so the admin can
                    decide which bucket to drop them into. */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.25, gap: 1 }}>
                  <Tooltip
                    title={
                      s.key === 'ungrouped_rollup' && s.unassignedCareers && s.unassignedCareers.length > 0
                        ? `${s.label}\n• ${s.unassignedCareers.join('\n• ')}`
                        : s.label
                    }
                    placement="top-start"
                    enterDelay={400}
                    componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 320 } } }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontWeight: isRollup(s.key) ? 400 : 500,
                        fontStyle: isRollup(s.key) ? 'italic' : 'normal',
                        cursor: s.key === 'ungrouped_rollup' ? 'help' : 'default',
                      }}
                    >
                      {s.label}
                    </Typography>
                  </Tooltip>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                    <Typography variant="body2" color="text.primary">
                      {fmt(s.count)} ({s.percent.toFixed(1)}%)
                    </Typography>
                    {variance != null && (
                      <Typography
                        variant="caption"
                        color={variance >= 0 ? 'success.main' : 'error.main'}
                        sx={{ minWidth: 52, textAlign: 'right' }}
                      >
                        {formatVariance(variance)}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Bar */}
                <Box
                  sx={{
                    width: '100%',
                    height: 8,
                    backgroundColor: '#f0f0f0',
                    borderRadius: 1,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: `${(s.percent / maxPct) * 100}%`,
                      height: '100%',
                      backgroundColor: isRollup(s.key) ? '#90a4ae' : '#4caf50',
                      borderRadius: 1,
                      transition: 'width 200ms ease',
                    }}
                  />
                </Box>

                {/* Bottom row: revenue + avg-per-patient. Hidden for zero-revenue rows. */}
                {s.revenue > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
                    <Typography variant="caption" color="text.secondary">
                      {fmtBaht(s.revenue)} · {t('EXECUTIVE.INSIGHT.OCCUPATION.OF_REVENUE', { p: s.revenuePercent.toFixed(1) })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('EXECUTIVE.INSIGHT.OCCUPATION.AVG_REVENUE')}: {fmtBaht(s.avgRevenuePerPatient)}
                    </Typography>
                  </Box>
                )}
              </Box>
            )
          })}
        </Stack>
    </WidgetCard>
  )
}
